import { Icon, Stack, Tooltip } from '@mui/material';
import { Typography } from '@mui/material';
import {
  AssessmentWithEntries,
  DATE_FORMAT_SHORT_FNS,
  FilterKeys,
  GroupType,
  SHORT_FORMATTED_DATE_FORMAT_FNS,
} from '@schooly/api';
import { AssessmentsArrangeBy } from '@schooly/components/filters';
import { AssessmentStatuses } from '@schooly/constants';
import { useAssessmentLabels } from '@schooly/hooks/use-assessment-labels';
import {
  AssessmentsIcon,
  NewTabIcon,
  ReportGeneratedIcon,
  SimpleButton,
  TagSubject,
  TypographyWithOverflowHint,
} from '@schooly/style';
import { newDateTimezoneOffset } from '@schooly/utils/date';
import { format } from 'date-fns';
import { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import useAppLocation from '../../../hooks/useAppLocation';
import { TagGroup } from '../../uikit/TagGroup/TagGroup';
import { ListViewAssessmentEntriesDateCell } from './ListViewAssessmentEntriesRow.styled';
import { ListViewAssessmentEntriesTagsCell } from './ListViewAssessmentEntriesRow.styled';
import { ListViewAssessmentEntriesNameCell } from './ListViewAssessmentEntriesRow.styled';

const getAssessmentLink = (assessment: AssessmentWithEntries) => {
  const groupId = assessment.groups[0]?.id;
  return `/assessments/${assessment.id}${groupId ? `?group_id=${groupId}` : ''}`;
};

type AssessmentEntriesRowContentInfoProps = {
  assessment: AssessmentWithEntries;
  fullView: boolean;
  arrangeBy: AssessmentsArrangeBy;
};

export const AssessmentEntriesRowContentInfo: FC<AssessmentEntriesRowContentInfoProps> = ({
  assessment,
  fullView,
  arrangeBy,
}) => {
  const location = useAppLocation();
  const { getAssessmentEvaluationTitle } = useAssessmentLabels();

  const group = assessment.groups[0];
  const showSubject = Boolean(group?.type === GroupType.TutorGroup || group?.subject?.id);
  const showGroup = Boolean((!showSubject || fullView) && group);

  const renderTags = () => {
    return (
      <>
        {showSubject && (
          <TagSubject
            archived={group?.subject?.archived}
            sx={{
              overflow: 'hidden',
              '& .MuiTypography-root': {
                textOverflow: 'ellipsis',
                overflow: 'hidden',
              },
            }}
          >
            {group?.type === GroupType.TutorGroup ? (
              <FormattedMessage id="groups-TutorGroup" />
            ) : (
              group?.subject?.name
            )}
          </TagSubject>
        )}
        {showGroup && <TagGroup>{group?.name}</TagGroup>}
      </>
    );
  };

  const renderName = () => {
    return (
      <TypographyWithOverflowHint
        component={Link}
        variant="h3"
        to={getAssessmentLink(assessment)}
        state={{ prevPathname: `${location.pathname}${location.hash ?? ''}` }}
        sx={{ '&:hover': { textDecoration: 'underline' } }}
      >
        {getAssessmentEvaluationTitle(assessment) ?? assessment.name}
      </TypographyWithOverflowHint>
    );
  };

  switch (arrangeBy) {
    case FilterKeys.Date:
      return (
        <>
          <ListViewAssessmentEntriesTagsCell sx={{ ...(fullView ? { flex: '0 0 300px' } : {}) }}>
            {renderTags()}
          </ListViewAssessmentEntriesTagsCell>

          <ListViewAssessmentEntriesNameCell>
            <AssessmentIcon assessment={assessment} />
            {renderName()}
          </ListViewAssessmentEntriesNameCell>
        </>
      );
    case FilterKeys.Subject:
      return (
        <>
          <ListViewAssessmentEntriesDateCell>
            {format(newDateTimezoneOffset(assessment.assessment_date), DATE_FORMAT_SHORT_FNS)}
          </ListViewAssessmentEntriesDateCell>

          <ListViewAssessmentEntriesNameCell>
            <AssessmentIcon assessment={assessment} />
            {renderName()}
          </ListViewAssessmentEntriesNameCell>
        </>
      );
    case FilterKeys.AreaOfLearning:
      return (
        <>
          <ListViewAssessmentEntriesDateCell>
            {format(newDateTimezoneOffset(assessment.assessment_date), DATE_FORMAT_SHORT_FNS)}
          </ListViewAssessmentEntriesDateCell>

          <Stack pr={1}>
            <AssessmentIcon assessment={assessment} />
          </Stack>

          <ListViewAssessmentEntriesTagsCell
            sx={(theme) => ({
              flex: '1 1 100%',
              [theme.breakpoints.down('lg')]: {
                flex: '1 1 100%',
              },
            })}
          >
            {renderTags()}
          </ListViewAssessmentEntriesTagsCell>
        </>
      );
    default:
      return null;
  }
};

const AssessmentIcon = ({ assessment }: { assessment: AssessmentWithEntries }) => {
  const { formatMessage } = useIntl();
  const location = useAppLocation();

  const isPublished = assessment.status === AssessmentStatuses.Published;

  return (
    <Tooltip
      componentsProps={{ tooltip: { sx: { p: 1.25, width: 200 } } }}
      title={
        <Stack alignItems="flex-start" gap={1}>
          <Stack>
            <Typography
              component={Link}
              to={getAssessmentLink(assessment)}
              state={{ prevPathname: `${location.pathname}${location.hash ?? ''}` }}
              sx={{ '&:hover': { textDecoration: 'underline', color: 'primary.main' } }}
            >
              {assessment.name}
            </Typography>
            <Typography color={isPublished ? 'success.main' : 'warning.main'}>
              {isPublished ? (
                <>
                  {formatMessage({ id: 'status-Published' })}{' '}
                  {assessment.publish_date
                    ? format(
                        newDateTimezoneOffset(assessment.publish_date),
                        SHORT_FORMATTED_DATE_FORMAT_FNS,
                      )
                    : ''}
                </>
              ) : (
                formatMessage({ id: 'status-NotPublished' })
              )}
            </Typography>

            {assessment.autogenerated && (
              <Typography mt={2}>
                {formatMessage(
                  { id: 'assessments-AutoGenerated' },
                  { reportName: assessment.reports[0]?.name },
                )}
              </Typography>
            )}
          </Stack>

          {assessment.generated_from_report_id && (
            <Link to={`/reports/${assessment.generated_from_report_id}`} target="_blank">
              <SimpleButton
                sx={(theme) => ({
                  marginTop: theme.spacing(1),
                  '& .MuiButton-startIcon .svg-icon': {
                    fontSize: theme.spacing(2),
                  },
                })}
                startIcon={<NewTabIcon />}
              >
                <Typography>{formatMessage({ id: 'assessments-GoToReport' })}</Typography>
              </SimpleButton>
            </Link>
          )}
        </Stack>
      }
    >
      <Icon
        sx={{
          color:
            assessment.status === AssessmentStatuses.Published ? 'success.main' : 'warning.main',
        }}
      >
        {assessment.autogenerated ? <ReportGeneratedIcon /> : <AssessmentsIcon />}
      </Icon>
    </Tooltip>
  );
};
